import { assertIsDefined, isDefined, isNotDefined } from '@sgme/fp';

import { getSignalRConnector } from '@/api/signalR/SignalRConnector.ts';
import type { WebapiEndpointBuilder } from '@/api/webapi.ts';
import { getCurrenciesFromCurrencyPair } from '@/App/tiles/FlexiForward/utils.ts';
import { flexiForwardApi } from '@/store/flexi-forward/api/index.ts';
import type {
  FlexiForwardRfsRequest,
  FlexiForwardRfsRequestFxoHedge,
  ReceivedFlexiForwardQuoteReply,
} from '@/store/flexi-forward/api/requestFlexiForwardRfs.model.ts';
import { getFlexiForwardRfsId } from '@/store/flexi-forward/helpers/rfs.ts';
import { getRequestFlexiForwardRfsMutationCacheKey } from '@/store/flexi-forward/selectors/rfs.ts';
import {
  flexiForwardRfsQuoteAborted,
  flexiForwardRfsQuoteReceived,
  flexiForwardRfsTraderNotificationReceived,
} from '@/store/flexi-forward/slice.ts';
import type { FxFlexiForward } from '@/store/flexi-forward/state.ts';
import type { AppDispatch, AppState } from '@/store/store.ts';
import { type IQuoteAbort, isQuoteTraderNotification, isQuoteWithPrice, type QuoteReply } from '@/utils/rfs/type.ts';

interface QuoteParams {
  tileId: string;
  rfsId: string;
  dispatch: AppDispatch;
  getState: () => AppState;
}

export function requestFlexiForwardRfs(builder: WebapiEndpointBuilder) {
  return builder.mutation<void, FlexiForwardRfsRequest>({
    query: (params) => {
      return {
        url: '/fxoAmericanForward/rfs/request',
        method: 'POST',
        body: params.body,
        headers: {
          'X-Correlation-Id': params.rfsId,
        },
      };
    },

    async onCacheEntryAdded(params, { dispatch, cacheDataLoaded, cacheEntryRemoved, getState }) {
      const { tileId, rfsId } = params;

      // wait for the initial query to resolve before proceeding
      await cacheDataLoaded;

      const quoteParams = { tileId, rfsId, dispatch, getState: getState as () => AppState };

      const handleReceiveQuoteReply = buildHandleReceiveQuoteReply(quoteParams);
      const handleReceiveQuoteAbort = buildHandleReceiveQuoteAbort(quoteParams);

      const signalRConnector = getSignalRConnector();
      signalRConnector.on('ReceiveQuoteReply', handleReceiveQuoteReply);
      signalRConnector.on('ReceiveQuoteWithdraw', handleReceiveQuoteReply);
      signalRConnector.on('ReceiveQuoteAbort', handleReceiveQuoteAbort);

      await cacheEntryRemoved;

      signalRConnector.off('ReceiveQuoteReply', handleReceiveQuoteReply);
      signalRConnector.off('ReceiveQuoteWithdraw', handleReceiveQuoteReply);
      signalRConnector.off('ReceiveQuoteAbort', handleReceiveQuoteAbort);
    },
  });
}

function buildHandleReceiveQuoteReply(quoteParams: QuoteParams) {
  return (message: QuoteReply<ReceivedFlexiForwardQuoteReply>) => {
    const { dispatch, tileId } = quoteParams;

    if (!isQuoteValid(message, quoteParams)) {
      return;
    }

    if (isQuoteWithPrice(message)) {
      dispatch(
        flexiForwardRfsQuoteReceived({
          tileId,
          message,
        }),
      );
    } else if (isQuoteTraderNotification(message)) {
      dispatch(
        flexiForwardRfsTraderNotificationReceived({
          tileId,
          message,
        }),
      );
    }
  };
}

function buildHandleReceiveQuoteAbort(quoteParams: QuoteParams) {
  return (quoteAbort: IQuoteAbort) => {
    const { dispatch, tileId } = quoteParams;

    if (isQuoteValid(quoteAbort, quoteParams)) {
      dispatch(flexiForwardRfsQuoteAborted({ tileId, reply: quoteAbort }));
    }

    closeFlexiForwardRfs(tileId, dispatch);
  };
}

export const createFlexiForwardRfsRequestParams = (
  tile: FxFlexiForward,
  tileId: string,
  rfsId: string,
  clientId: number,
  signalRConnectionId: string | undefined,
  forceTradable = false,
): FlexiForwardRfsRequest | undefined => {
  const flexiForwardProperties = tile.properties;

  const {
    currencyPair,
    clientsWay,
    startDate,
    expiryDate,
    deliveryDate,
    premiumDate,
    cutOffMarketPlace,
    solvable,
    solvingType,
  } = flexiForwardProperties;

  const { currency1, currency2 } = getCurrenciesFromCurrencyPair(currencyPair);

  const { notional, notionalCurrencyIndex, premiumWay, upfrontCurrency, upfront, rate } = flexiForwardProperties;

  const flexiForwardHedges = flexiForwardProperties.hedgeType !== 'Live' ? [getHedge(flexiForwardProperties)] : [];

  if (
    isNotDefined(currencyPair) ||
    isNotDefined(currency1) ||
    isNotDefined(currency2) ||
    isNotDefined(startDate) ||
    isNotDefined(expiryDate) ||
    isNotDefined(deliveryDate) ||
    isNotDefined(premiumDate) ||
    isNotDefined(signalRConnectionId)
  ) {
    return undefined;
  }

  const isStrikeSolving = isDefined(solvable) ? solvable === 'ForwardRate' : solvingType !== 'solveUpfront';

  const premiumKey = premiumWay === 'Bid' ? 'premiumBidValue' : 'premiumAskValue';
  const premiumValue = isStrikeSolving ? { [premiumKey]: upfront } : {};

  const callabilityStyle = getCallabilityStyle(tile.properties.callabilityType);

  const productLeg = {
    id: tileId,
    callabilityStartDate: new Date(startDate),
    callabilityStyle,
    cutOffMarketPlace: cutOffMarketPlace ?? tile.properties.possibleCutOffMarketPlaces?.default ?? '',
    deliveryDate: new Date(deliveryDate),
    expiryDate: new Date(expiryDate),
    premiumCurrency: upfrontCurrency ?? currency1,
    premiumDeliveryDate: new Date(premiumDate),
    notionalAmount: notional ?? 0,
    notionalCurrency: notionalCurrencyIndex ?? 1,
    ...premiumValue,
    putOrCall: 'Call',
    settlementType: 'Physical',
    side: clientsWay ?? 'Buy',
    strikeInDelta: null,
    strikePrice: isStrikeSolving ? null : (rate ?? 0),
    strikeShortcut: null,
    product: 'AmericanForward',
  } as const;

  return {
    tileId,
    rfsId,
    body: {
      fxOptionProduct: {
        amountReference: null,
        amountReferenceCurrency: null,
        currencyPair,
        hedges: flexiForwardHedges,
        legs: [productLeg],
        markupCurrency: 1,
        premiumCurrency: upfrontCurrency === currency1 ? 1 : 2,
        premiumType: 'AMOUNT',
        skipCreditCheck: false,
        strategyType: 'AmericanForward',
        isSolving: isStrikeSolving ? true : undefined,
      },
      replyToStream: signalRConnectionId,
      rfsId,
      forceTradable,

      requesterCounterpartyId: clientId,
    },
  };
};

function getHedge({
  spotHedge: maturityDate,
  deltaHedgeAmount,
  deltaHedgeCurrencyIndex,
}: FxFlexiForward['properties']): FlexiForwardRfsRequestFxoHedge {
  assertIsDefined(maturityDate, 'hedgePaymentDate is mandatory for pricing when hedgeType is Spot or Forward');

  return {
    hedgeType: 'Spot',
    maturityDate: new Date(maturityDate),
    notionalAmount: deltaHedgeAmount?.toString() ?? '',
    notionalCurrency: deltaHedgeCurrencyIndex ?? 1,
    price: '',
  };
}

const getCallabilityStyle = (
  callabilityStyle: string | undefined,
): 'CallabilityWindow' | 'CallabilityAmerican' | 'CallabilityStyleUnknown' => {
  switch (callabilityStyle) {
    case 'Windows':
      return 'CallabilityWindow';
    case 'American':
      return 'CallabilityAmerican';

    default:
      return 'CallabilityStyleUnknown';
  }
};

/**
 * @description Closes the Flexi forward RFS by removing the mutation result from the cache
 * @description To close the listeners, call closeCashRfs(tileId, dispatch)
 * @description for request called with:
 * @description const [requestCashRfs] = useRequestCashRfsMutation({ fixedCacheKey: getRequestCashRfsMutationCacheKey(tileId) });
 * @param {string} tileId - The ID of the tile associated with the RFS
 * @param {AppDispatch} dispatch - The Redux dispatch function
 */
export const closeFlexiForwardRfs = (tileId: string, dispatch: AppDispatch) => {
  dispatch(
    flexiForwardApi.internalActions.removeMutationResult({
      fixedCacheKey: getRequestFlexiForwardRfsMutationCacheKey(tileId),
    }),
  );
};

function isQuoteValid(message: QuoteReply<ReceivedFlexiForwardQuoteReply>, quoteParams: QuoteParams): boolean {
  const { rfsId, tileId, getState } = quoteParams;

  if (message.RequestId !== rfsId) {
    return false;
  }

  if (isQuoteWithPrice(message) && isNotDefined(message.OptionQuoteReply?.LegsData)) {
    return false;
  }

  const state = getState();
  const flexiForward = state.flexiForward[tileId];
  const flexiForwardRfsId = isDefined(flexiForward) ? getFlexiForwardRfsId(flexiForward.properties.rfs) : undefined;

  if (isNotDefined(flexiForwardRfsId)) {
    // rfs was just cancelled
    return false;
  }

  return message.RequestId === flexiForwardRfsId;
}
